import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { ApiEnums } from '@app/api';
import {
  AskFrequency,
  AskFrequencyKind,
  askFrequencyMaxCount,
  isRepeatingKind,
  isValidAskFrequencyCount,
  parseWhenToAsk,
  toWhenToAsk,
} from './ask-frequency';

export type AskFrequencyFormGroup = FormGroup<{
  kind: FormControl<AskFrequencyKind>;
  count: FormControl<number | null>;
  countingScope: FormControl<ApiEnums.CountingScope>;
}>;

export const DEFAULT_ASK_FREQUENCY: AskFrequency = {
  whenToAsk: null,
  countingScope: ApiEnums.CountingScope.Venue,
};

/** The count only applies to weeks and months, and its limit depends on which. */
function countValidator(control: AbstractControl<number | null>): ValidationErrors | null {
  const kind = control.parent?.get('kind')?.value as AskFrequencyKind | undefined;
  return isRepeatingKind(kind) && !isValidAskFrequencyCount(kind, control.value)
    ? { askFrequencyCount: { max: askFrequencyMaxCount(kind) } }
    : null;
}

export function createAskFrequencyGroup(
  value: AskFrequency = DEFAULT_ASK_FREQUENCY
): AskFrequencyFormGroup {
  const { kind, count, countingScope } = askFrequencyFormValue(value);
  const group: AskFrequencyFormGroup = new FormGroup({
    kind: new FormControl(kind, { nonNullable: true }),
    count: new FormControl<number | null>(count, countValidator),
    countingScope: new FormControl(countingScope, { nonNullable: true }),
  });

  // Both controls belong to the group, so this subscription lives and dies with it.
  group.controls.kind.valueChanges.subscribe(kind => {
    if (isRepeatingKind(kind) && group.controls.count.value === null) {
      group.controls.count.setValue(1, { emitEvent: false });
    } else {
      group.controls.count.updateValueAndValidity({ emitEvent: false });
    }
  });

  return group;
}

export type AskFrequencyFormValue = ReturnType<AskFrequencyFormGroup['getRawValue']>;

export function askFrequencyFormValue({
  whenToAsk,
  countingScope,
}: AskFrequency): AskFrequencyFormValue {
  return { ...parseWhenToAsk(whenToAsk), countingScope };
}

export function setAskFrequencyGroupValue(group: AskFrequencyFormGroup, value: AskFrequency): void {
  group.setValue(askFrequencyFormValue(value));
}

export function askFrequencyFromValue({
  kind,
  count,
  countingScope,
}: AskFrequencyFormValue): AskFrequency {
  return { whenToAsk: toWhenToAsk(kind, count), countingScope };
}

/** The value to save, or every time when the count is out of range, as it can be while hidden. */
export function savableAskFrequency(value: AskFrequencyFormValue): AskFrequencyFormValue {
  return isValidAskFrequencyCount(value.kind, value.count)
    ? value
    : askFrequencyFormValue({ whenToAsk: null, countingScope: value.countingScope });
}

export function askFrequencyFromGroup(group: AskFrequencyFormGroup): AskFrequency {
  return askFrequencyFromValue(group.getRawValue());
}
